import { Stack } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useExclusionGroups } from '../../api/exclusion-groups';
import { useIsAdmin } from '../../auth/useAuth';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonLoader } from '../../components/SkeletonLoader';
import { Table } from '../../components/Table';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { formatRelativeTime } from '../../utils/format';
import shared from '../shared.module.css';

export function ExclusionGroupListPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const isAdmin = useIsAdmin();

	const { data: groups, isLoading, error } = useExclusionGroups(id ?? '');
	const breadcrumbs = useProjectBreadcrumbs('Exclusion Groups');

	const columns = [
		{ key: 'name', label: 'Name' },
		{ key: 'experiments', label: 'Experiments' },
		{ key: 'created', label: 'Created' },
	];

	return (
		<div>
			<PageHeader
				title="Exclusion Groups"
				breadcrumbs={breadcrumbs}
				actions={
					isAdmin ? (
						<Button
							variant="primary"
							onClick={() => navigate(`/projects/${id}/exclusion-groups/new`)}
						>
							New Group
						</Button>
					) : undefined
				}
			/>

			<div className={shared.content}>
				<Card>
					{error ? (
						<p className={shared.errorMessage}>
							{error instanceof Error ? error.message : 'Failed to load exclusion groups'}
						</p>
					) : (
						<Table>
							<Table.Header columns={columns} />
							<Table.Body>
								{isLoading ? (
									<SkeletonLoader rows={3} columns={3} />
								) : !groups || groups.length === 0 ? (
									<tr>
										<td colSpan={3}>
											<EmptyState
												icon={Stack}
												title="No exclusion groups yet"
												description="Exclusion groups prevent visitors from being enrolled in overlapping experiments."
												action={
													isAdmin
														? {
																label: 'New Group',
																onClick: () => navigate(`/projects/${id}/exclusion-groups/new`),
															}
														: undefined
												}
											/>
										</td>
									</tr>
								) : (
									groups.map((group) => (
										<Table.Row
											key={group.id}
											onClick={() => navigate(`/projects/${id}/exclusion-groups/${group.id}`)}
										>
											<Table.Cell>{group.name}</Table.Cell>
											<Table.Cell mono>{group.allocations.length}</Table.Cell>
											<Table.Cell mono>{formatRelativeTime(group.created_at)}</Table.Cell>
										</Table.Row>
									))
								)}
							</Table.Body>
						</Table>
					)}
				</Card>
			</div>
		</div>
	);
}
